import {inject, Pipe, PipeTransform} from "@angular/core";
import {
	AssessmentState,
	ControlPanelType,
	EventType,
	Gender,
	ParticipantType,
	RoomObservationState,
	SessionPhaseType,
	SessionType,
	TranslationKey,
	WebClientType,
} from "@models";
import {TranslateService} from "@ngx-translate/core";
import {Translations} from "@translations";

export enum EnumLabel {
	ExamParticipant,
	ExamParticipantPlural,
	TrainingParticipant,
	SessionPhase,
	EventType,
	Gender,
	SessionType,
	RoomObservationState,
	AssessmentState,
	WebClientType,
	ControlPanelHeader,
}

type EnumMapping = Record<number, TranslationKey>;

const Participant_Mappings: EnumMapping = {
	[ParticipantType.Learner]: "PARTICIPANT_TYPE_STUDENT",
	[ParticipantType.Instructor]: "PARTICIPANT_TYPE_EXAMINER",
	[ParticipantType.StandardizedPatient]: "PARTICIPANT_TYPE_STANDARDIZED_PATIENT",
};

const TeamTrainingParticipant_Mappings: EnumMapping = {
	[ParticipantType.Learner]: "PARTICIPANT_TYPE_LEARNER",
	[ParticipantType.Instructor]: "PARTICIPANT_TYPE_INSTRUCTOR",
	[ParticipantType.StandardizedPatient]: "PARTICIPANT_TYPE_STANDARDIZED_PATIENT",
};

const ParticipantPlural_Mappings: EnumMapping = {
	[ParticipantType.Learner]: "PARTICIPANT_TYPE_STUDENT_PLURAL",
	[ParticipantType.Instructor]: "PARTICIPANT_TYPE_EXAMINER_PLURAL",
	[ParticipantType.StandardizedPatient]: "PARTICIPANT_TYPE_STANDARDIZED_PATIENT_PLURAL_ABBRV",
};

const SessionPhase_Mappings: EnumMapping = {
	[SessionPhaseType.Intermission]: "SESSION_PHASE_TYPE_INTERMISSION",
	[SessionPhaseType.Confirmation]: "SESSION_PHASE_TYPE_CONFIRMATION",
	[SessionPhaseType.StudentReady]: "SESSION_PHASE_TYPE_STUDENT_READY",
	[SessionPhaseType.PreEncounter]: "SESSION_PHASE_TYPE_PRE_ENCOUNTER",
	[SessionPhaseType.Encounter]: "SESSION_PHASE_TYPE_ENCOUNTER",
	[SessionPhaseType.PostEncounter]: "SESSION_PHASE_TYPE_POST_ENCOUNTER",
};

const EventType_Mappings: EnumMapping = {
	[EventType.Simulation]: "EVENT_TYPE_SIMULATION",
	[EventType.Exam]: "EVENT_TYPE_EXAM",
	[EventType.PeerPractice]: "EVENT_TYPE_PEER_PRACTICE",
	[EventType.MultiRoomRecording]: "EVENT_TYPE_MULTI_ROOM_RECORDING",
	[EventType.AutomaticRecording]: "EVENT_TYPE_AUTOMATIC_RECORDING",
};

const Gender_Mappings: EnumMapping = {
	[Gender.Male]: "GENDER_Male",
	[Gender.Female]: "GENDER_Female",
	[Gender.Other]: "GENDER_Other",
};

const SessionType_Mappings: EnumMapping = {
	[SessionType.Exam]: "SESSION_TYPE_EXAM",
	[SessionType.PeerPractice]: "SESSION_TYPE_PEER_PRACTICE",
	[SessionType.TeamTrainingSession]: "SESSION_TYPE_TEAM_TRAINING",
	[SessionType.NonSimulation]: "SESSION_TYPE_NON_SIMULATION",
	[SessionType.MultiRoomRecording]: "SESSION_TYPE_MULTI_ROOM_RECORDING",
	[SessionType.AutomaticRecording]: "SESSION_TYPE_AUTOMATIC_RECORDING",
};

const RoomObservationState_Mappings: EnumMapping = {
	[RoomObservationState.Observing]: "ROOM_STATE_OBSERVING",
	[RoomObservationState.NotObserving]: "ROOM_STATE_NOT_OBSERVING",
	[RoomObservationState.Recording]: "ROOM_STATE_RECORDING",
	[RoomObservationState.Unknown]: "ROOM_STATE_Unknown",
};

const AssessmentState_Mappings: EnumMapping = {
	[AssessmentState.InProgress]: "ASSESSMENT_STATE_IN_PROGRESS",
	[AssessmentState.Aborted]: "ASSESSMENT_STATE_ABORTED",
	[AssessmentState.Failed]: "ASSESSMENT_STATE_FAILED",
	[AssessmentState.InReview]: "ASSESSMENT_STATE_IN_REVIEW",
	[AssessmentState.Passed]: "ASSESSMENT_STATE_PASSED",
};

const WebClientType_Mappings: EnumMapping = {
	[WebClientType.MoC]: "WEB_CLIENT_TYPE_MC",
	[WebClientType.ViewingStation]: "WEB_CLIENT_TYPE_VIEWING_STATION",
	[WebClientType.Tablet]: "WEB_CLIENT_TYPE_TABLET",
	[WebClientType.External]: "WEB_CLIENT_TYPE_EXTERNAL",
	[WebClientType.Player]: "WEB_CLIENT_TYPE_PLAYER",
	[WebClientType.Recorder]: "WEB_CLIENT_TYPE_RECORDER",
	[WebClientType.System]: "WEB_CLIENT_TYPE_SYSTEM",
	[WebClientType.TrainingRoomEntrance]: "WEB_CLIENT_TYPE_ROOM_ENTRANCE",
};

const ControlPanelHeader_Mappings: EnumMapping = {
	[ControlPanelType.Annotation]: "PANEL_HEADER_ANNOTATION",
	[ControlPanelType.Checklist]: "PANEL_HEADER_CHECKLIST",
	[ControlPanelType.Assessment]: "PANEL_HEADER_ASSESSMENT",
	[ControlPanelType.Announcement]: "PANEL_HEADER_ANNOUNCEMENT",
	[ControlPanelType.Microphone]: "PANEL_HEADER_MICROPHONE",
	[ControlPanelType.Transmission]: "PANEL_HEADER_TRANSMISSION",
	[ControlPanelType.Audience]: "PANEL_HEADER_AUDIENCE",
	[ControlPanelType.MediaScreens]: "PANEL_HEADER_MEDIASCREENS",
	[ControlPanelType.MediaLibrary]: "PANEL_HEADER_MEDIALIBRARY",
	[ControlPanelType.TrainTheTrainer]: "PANEL_HEADER_TRAINTHETRAINER",
	[ControlPanelType.Operator]: "PANEL_HEADER_OPERATOR",
	[ControlPanelType.VoiceOfPatient]: "PANEL_HEADER_VOICEOFPATIENT",
	[ControlPanelType.Export]: "PANEL_HEADER_EXPORT",
	[ControlPanelType.AudioChannels]: "PANEL_HEADER_AUDIOCHANNELS",
	[ControlPanelType.ViewManagement]: "PANEL_HEADER_VIEWMANAGEMENT",
	[ControlPanelType.Room]: "PANEL_HEADER_ROOM",
	[ControlPanelType.EventSelection]: "PANEL_HEADER_EVENTSELECTION",
};

const EnumLabel_Mapping: Record<EnumLabel, EnumMapping> = {
	[EnumLabel.ExamParticipant]: Participant_Mappings,
	[EnumLabel.ExamParticipantPlural]: ParticipantPlural_Mappings,
	[EnumLabel.TrainingParticipant]: TeamTrainingParticipant_Mappings,
	[EnumLabel.SessionPhase]: SessionPhase_Mappings,
	[EnumLabel.EventType]: EventType_Mappings,
	[EnumLabel.Gender]: Gender_Mappings,
	[EnumLabel.RoomObservationState]: RoomObservationState_Mappings,
	[EnumLabel.SessionType]: SessionType_Mappings,
	[EnumLabel.AssessmentState]: AssessmentState_Mappings,
	[EnumLabel.WebClientType]: WebClientType_Mappings,
	[EnumLabel.ControlPanelHeader]: ControlPanelHeader_Mappings,
};

@Pipe({
	name: "enumLabel",
	standalone: true,
})
export class EnumLabelPipe implements PipeTransform {
	private _translateService = inject(TranslateService);

	transform(value: number, enumType: EnumLabel): string {
		const mappingObject = EnumLabel_Mapping[enumType];
		const labelKey = mappingObject[value];
		return this._translateService.instant(Translations[labelKey]);
	}
}
